<script setup lang="ts">
  import type {
    ChangeStakeholderInfoRequestPayload,
    Stakeholder
  } from '@gat-ship/types'
  import { testIds } from '@gat-ship/types'
  import {
    FormField,
    FormSubmit,
    InputGroupInput,
    InputGroupPhone,
    isValidPhoneNumber,
    Button,
    Modal,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalHeaderDescription,
    ModalHeaderTitle,
    ModalSection,
    useForm,
    z
  } from '@gat-ship/vue-components'
  import { FieldGroup } from '@gat-ship/vue-components'
  import { ref } from 'vue'
  import { useStakeholderContext } from '../../composables/useStakeholder'
  import { useStakeholderDetail } from '../providers/stakeholder-detail-context'
  import type { ShowStakeholderUpdateGeneralInfoParams } from '../providers/stakeholder-modals'
  import { CustomFieldForm } from '@gat-ship/custom-fields'
  import { useStakeholderMatchMessages } from '@gat-ship/stakeholder-create'
  import { text } from '@gat-ship/stakeholders'
  import { CountryPicker } from '@gat-ship/ref-data'

  const isOpen = defineModel<boolean>('open', { default: false })
  const service = useStakeholderContext()
  const { dynamicFields } = useStakeholderDetail()

  // Preserve the old FormPhoneField blur-time format validation (optional
  // field: empty allowed, non-empty must be a valid phone number).
  const phoneValidators = {
    onBlur: ({ value }: { value: string }) =>
      !value || value.length === 0
        ? undefined
        : isValidPhoneNumber(value)
          ? undefined
          : 'Invalid phone number'
  }

  const _stakeholder = ref<Stakeholder | undefined>(undefined)
  const customFieldValues = ref<Record<string, unknown>>({})
  const dynCustomFieldsRef = ref<InstanceType<typeof CustomFieldForm> | null>(
    null
  )
  const modalTitle = ref('')
  const modalDescription = ref('')

  const { buildMatchMessages } = useStakeholderMatchMessages({
    excludeStakeholder: () => _stakeholder.value
  })

  const nameMessagesAsync = buildMatchMessages(
    () => ({
      type: _stakeholder.value?.stakeholderType ?? 'client',
      name_matching: 'similar',
      name: '',
      limit: 6
    }),
    {
      exactMatchMessage: 'This name is already in use',
      similarMessage: (count) =>
        `${count} ${_stakeholder.value?.stakeholderType}(s) with similar names found`
    }
  )
  const emailMessagesAsync = buildMatchMessages(
    () => ({
      type: _stakeholder.value?.stakeholderType ?? 'client',
      name_matching: 'exact',
      email: ''
    }),
    {
      exactMatchMessage: `A ${_stakeholder.value?.stakeholderType ?? 'stakeholder'} with this email already exists`,
      variant: 'warning'
    }
  )
  const phoneMessagesAsync = buildMatchMessages(
    () => ({
      type: _stakeholder.value?.stakeholderType ?? 'client',
      name_matching: 'exact',
      phone: ''
    }),
    {
      exactMatchMessage: `A ${_stakeholder.value?.stakeholderType ?? 'stakeholder'} with this phone number already exists`,
      variant: 'warning'
    }
  )

  // Simple email shape check that matches the backend's validator well
  // enough — we don't need RFC-compliant parsing client-side, just catch
  // obvious typos (missing "@", missing TLD) before the API rejects.
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

  const generalInfoSchema = z.object({
    name: z.string().min(1, 'Name is required'),
    shortName: z.string().optional(),
    email: z
      .string()
      .optional()
      .refine(
        (v) => !v || emailRegex.test(v),
        'Please enter a valid email address'
      ),
    phoneNumber: z.string().optional(),
    countryCode: z.string().min(1, 'Country is required')
  })
  type GeneralInfoFormValues = z.infer<typeof generalInfoSchema>

  const form = useForm({
    defaultValues: {
      name: '',
      shortName: '',
      email: '',
      phoneNumber: '',
      countryCode: ''
    } as GeneralInfoFormValues,
    validators: {
      onChange: generalInfoSchema
    },
    onSubmit: async ({ value }) => {
      if (dynCustomFieldsRef.value) {
        const valid = await dynCustomFieldsRef.value.validate()
        if (!valid) return
      }
      const stakeholder = _stakeholder.value
      if (!stakeholder) {
        throw new Error('Stakeholder not found.')
      }
      // Nullable fields must be `null` (not `""`) when empty — the
      // backend rejects `""` for email / phoneNumber because they don't
      // match the expected format.
      const payload: ChangeStakeholderInfoRequestPayload = {
        name: value.name,
        shortName: value.shortName || null,
        email: value.email || null,
        phoneNumber: value.phoneNumber || null,
        countryCode: value.countryCode
      }
      const hasCustomFields = dynamicFields.customFields.value.length > 0
      await service?.commands.changeGeneralInfo(
        stakeholder.id,
        payload,
        hasCustomFields ? customFieldValues.value : undefined
      )
      isOpen.value = false
    }
  })

  function seedGeneralInfoForm(
    stakeholder: ShowStakeholderUpdateGeneralInfoParams['stakeholder']
  ) {
    form.reset({
      name: stakeholder.name ?? '',
      shortName: stakeholder.shortName ?? '',
      email: stakeholder.email ?? '',
      phoneNumber: stakeholder.phoneNumber ?? '',
      countryCode: stakeholder.country?.code ?? ''
    })
  }

  function showModal(props: ShowStakeholderUpdateGeneralInfoParams) {
    _stakeholder.value = props.stakeholder
    modalTitle.value = text.groups.generalInformation(
      props.stakeholder.stakeholderType
    ).modals.editGeneral.title
    modalDescription.value = text.groups
      .generalInformation(props.stakeholder.stakeholderType)
      .modals.editGeneral.description(props.stakeholder?.name ?? '')
    customFieldValues.value = { ...service?.customFieldValues.value }
    seedGeneralInfoForm(props.stakeholder)
    isOpen.value = true
  }

  defineExpose({ showModal })
</script>

<template>
  <modal v-model:open="isOpen">
    <ModalHeader>
      <ModalHeaderTitle>
        {{ modalTitle }}
      </ModalHeaderTitle>
      <ModalHeaderDescription v-if="modalDescription">
        {{ modalDescription }}
      </ModalHeaderDescription>
    </ModalHeader>
    <ModalContent>
      <ModalSection>
        <FieldGroup>
          <FormField
            :form="form"
            name="name"
            label="Name"
            required
            v-bind="dynamicFields.fieldProps('name')"
            :messages-async="nameMessagesAsync"
          >
            <template #default="{ field }">
              <InputGroupInput
                :id="field.name"
                :model-value="field.state.value ?? ''"
                autofocus
                :data-testid="testIds.stakeholder.forms.generalInfo.nameInput"
                @update:model-value="field.handleChange"
                @blur="field.handleBlur"
              />
            </template>
          </FormField>
          <FormField
            :form="form"
            name="shortName"
            label="Short Name"
            v-bind="dynamicFields.fieldProps('shortName')"
          >
            <template #default="{ field }">
              <InputGroupInput
                :id="field.name"
                :model-value="field.state.value ?? ''"
                :data-testid="
                  testIds.stakeholder.forms.generalInfo.shortNameInput
                "
                @update:model-value="field.handleChange"
                @blur="field.handleBlur"
              />
            </template>
          </FormField>
          <FormField
            :form="form"
            name="countryCode"
            label="Country"
            required
            v-bind="dynamicFields.fieldProps('countryCode')"
          >
            <template #default="{ field, invalid }">
              <CountryPicker
                :id="field.name"
                :model-value="field.state.value"
                :aria-invalid="invalid"
                :data-testid="
                  testIds.stakeholder.forms.generalInfo.countryInput
                "
                @update:model-value="field.handleChange"
                @blur="field.handleBlur"
              />
            </template>
          </FormField>
        </FieldGroup>
        <FieldGroup
          v-if="
            dynamicFields.isSystemFieldVisible('email') ||
            dynamicFields.isSystemFieldVisible('phoneNumber')
          "
        >
          <FormField
            v-if="dynamicFields.isSystemFieldVisible('email')"
            :form="form"
            name="email"
            label="Email"
            :messages-async="emailMessagesAsync"
            v-bind="dynamicFields.fieldProps('email')"
          >
            <template #default="{ field }">
              <InputGroupInput
                :id="field.name"
                :model-value="field.state.value ?? ''"
                :data-testid="testIds.stakeholder.forms.generalInfo.emailInput"
                @update:model-value="field.handleChange"
                @blur="field.handleBlur"
              />
            </template>
          </FormField>
          <FormField
            v-if="dynamicFields.isSystemFieldVisible('phoneNumber')"
            :form="form"
            name="phoneNumber"
            label="Phone"
            :validators="phoneValidators"
            :messages-async="phoneMessagesAsync"
            v-bind="dynamicFields.fieldProps('phoneNumber')"
          >
            <template #default="{ field }">
              <InputGroupPhone
                :id="field.name"
                :model-value="field.state.value ?? undefined"
                :country-code="form.state.values.countryCode"
                :data-testid="testIds.stakeholder.forms.generalInfo.phoneInput"
                @update:model-value="(v) => field.handleChange(v ?? '')"
                @blur="field.handleBlur"
              />
            </template>
          </FormField>
        </FieldGroup>
        <CustomFieldForm
          v-if="dynamicFields.customFields.value.length > 0"
          ref="dynCustomFieldsRef"
          v-model="customFieldValues"
        />
      </ModalSection>
    </ModalContent>
    <ModalFooter>
      <FormSubmit :form="form" label="Save Changes" />
      <Button variant="ghost" @click="isOpen = false"> Cancel </Button>
    </ModalFooter>
  </modal>
</template>
